"use client";

import { createContext, useContext, useReducer, useEffect, useCallback, useState, useRef } from "react";
import { useSession } from "next-auth/react";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { trackEvent } from "@/lib/track";
import { GIFT_WRAP_PRICE } from "@/lib/pricing";

export const MAX_QUANTITY_PER_ITEM = 10;
export { GIFT_WRAP_PRICE };

export interface CartItemData {
  id: string;
  productId?: string;
  categoryId?: string;
  /** Slug + category slug let the checkout / cart pages link each line
   *  item back to its product page. Optional so legacy cart contents in
   *  localStorage from before this field existed still render. */
  slug?: string;
  categorySlug?: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  customConfig?: Record<string, string>;
  /** Gift wrap toggle for this line item ($GIFT_WRAP_PRICE per unit) */
  giftWrap?: boolean;
  /** Optional gift message — only meaningful when giftWrap is true */
  giftMessage?: string;
  /** Pre-order flag — display only. The server is the source of truth and
   *  re-derives this from product.preorderEnabled at checkout time so a
   *  client can't fake a pre-order on a non-preorder product. */
  isPreorder?: boolean;
  /** Snapshot of expected ship date at the moment the item was added.
   *  Used purely for display in the cart drawer / checkout. */
  preorderShipDate?: string;
  /** Specific in-production WigAsset the customer reserved from a PDP
   *  card. When set, the line item locks one unique physical wig (not
   *  generic stock); /api/checkout atomically claims the asset by
   *  setting WigAsset.orderItemId at order creation. */
  wigAssetId?: string;
  /** Display: barcode of the reserved wig, e.g. "WIG-00042". Shown in
   *  the cart drawer + checkout summary so customer knows which specific
   *  physical wig is locked. */
  wigAssetBarcode?: string;
  /** ProductVariant.id chosen on the PDP (e.g. "Light Blue" of a denim
   *  cap that has color variants). Forwarded to /api/checkout so the
   *  resulting OrderItem records which variant was sold — drives
   *  variant-level stock decrement + correct admin display. */
  variantId?: string;
  /** SKU snapshot of the chosen variant. Mirrored into OrderItem so
   *  packing slips still resolve the right code even if the variant
   *  gets renamed later. */
  variantSku?: string;
}

interface CartState {
  items: CartItemData[];
  isOpen: boolean;
}

type CartAction =
  | { type: "ADD_ITEM"; payload: CartItemData }
  | { type: "REMOVE_ITEM"; payload: string }
  | { type: "UPDATE_QUANTITY"; payload: { id: string; quantity: number } }
  | { type: "SET_GIFT_WRAP"; payload: { id: string; giftWrap: boolean; giftMessage?: string } }
  | { type: "SET_GIFT_MESSAGE"; payload: { id: string; giftMessage: string } }
  | { type: "CLEAR" }
  | { type: "TOGGLE_CART" }
  | { type: "OPEN_CART" }
  | { type: "CLOSE_CART" }
  | { type: "LOAD"; payload: CartItemData[] };

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case "ADD_ITEM": {
      const existing = state.items.find(
        (item) =>
          item.productId === action.payload.productId &&
          JSON.stringify(item.customConfig) ===
            JSON.stringify(action.payload.customConfig)
      );
      if (existing) {
        const newQty = Math.min(existing.quantity + action.payload.quantity, MAX_QUANTITY_PER_ITEM);
        return {
          ...state,
          isOpen: true,
          items: state.items.map((item) =>
            item.id === existing.id
              ? { ...item, quantity: newQty }
              : item
          ),
        };
      }
      return {
        ...state,
        isOpen: true,
        items: [...state.items, { ...action.payload, quantity: Math.min(action.payload.quantity, MAX_QUANTITY_PER_ITEM) }],
      };
    }
    case "REMOVE_ITEM":
      return {
        ...state,
        items: state.items.filter((item) => item.id !== action.payload),
      };
    case "UPDATE_QUANTITY": {
      if (action.payload.quantity <= 0) {
        return {
          ...state,
          items: state.items.filter((item) => item.id !== action.payload.id),
        };
      }
      const clampedQty = Math.min(action.payload.quantity, MAX_QUANTITY_PER_ITEM);
      return {
        ...state,
        items: state.items.map((item) =>
          item.id === action.payload.id
            ? { ...item, quantity: clampedQty }
            : item
        ),
      };
    }
    case "SET_GIFT_WRAP":
      return {
        ...state,
        items: state.items.map((item) =>
          item.id === action.payload.id
            ? {
                ...item,
                giftWrap: action.payload.giftWrap,
                giftMessage: action.payload.giftWrap
                  ? action.payload.giftMessage ?? item.giftMessage ?? ""
                  : "",
              }
            : item
        ),
      };
    case "SET_GIFT_MESSAGE":
      return {
        ...state,
        items: state.items.map((item) =>
          item.id === action.payload.id
            ? { ...item, giftMessage: action.payload.giftMessage }
            : item
        ),
      };
    case "CLEAR":
      return { ...state, items: [] };
    case "TOGGLE_CART":
      return { ...state, isOpen: !state.isOpen };
    case "OPEN_CART":
      return { ...state, isOpen: true };
    case "CLOSE_CART":
      return { ...state, isOpen: false };
    case "LOAD":
      return { ...state, items: action.payload };
    default:
      return state;
  }
}

export interface GuestCheckoutItem {
  productId?: string;
  quantity: number;
  customConfig?: Record<string, string>;
  customPrice?: number;
  productName?: string;
  giftWrap?: boolean;
  giftMessage?: string;
  /** Specific in-production WigAsset reserved on the PDP. Server claims
   *  it atomically when creating the OrderItem. */
  wigAssetId?: string;
}

interface CartContextType {
  items: CartItemData[];
  isOpen: boolean;
  addItem: (item: Omit<CartItemData, "id">) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  setGiftWrap: (id: string, giftWrap: boolean, giftMessage?: string) => void;
  setGiftMessage: (id: string, giftMessage: string) => void;
  clearCart: () => void;
  toggleCart: () => void;
  openCart: () => void;
  closeCart: () => void;
  subtotal: number;
  giftWrapTotal: number;
  itemCount: number;
  maxQuantityReached: string | null;
  clearMaxQuantityMessage: () => void;
  getCartItemsForCheckout: () => GuestCheckoutItem[];
  /** Increments on every addItem call so the header cart icon can bounce */
  cartBounceKey: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, {
    items: [],
    isOpen: false,
  });
  const [maxQuantityReached, setMaxQuantityReached] = useState<string | null>(null);
  const [cartBounceKey, setCartBounceKey] = useState(0);

  useEffect(() => {
    const saved = localStorage.getItem("cart");
    if (saved) {
      try {
        const items: CartItemData[] = JSON.parse(saved);
        // Backfill images for custom-built wigs that were added before the fix
        const patched = items.map((item) => {
          if (item.image || !item.customConfig) return item;
          const cfg = item.customConfig;
          const toSlug = (v: string) =>
            v.toLowerCase().replace(/['"]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
          const hasCombo = cfg.texture && cfg.color && cfg.color !== "Custom Color";
          const combo = hasCombo
            ? `/builder/combo/${toSlug(cfg.texture)}--${toSlug(cfg.color)}.webp`
            : null;
          const fallback = (["color", "texture", "length", "base_type"] as const).reduce<string | null>(
            (found, key) => found || (cfg[key] ? `/builder/${key}/${toSlug(cfg[key])}.webp` : null),
            null
          );
          return { ...item, image: combo || fallback || undefined };
        });
        dispatch({ type: "LOAD", payload: patched });

        // Server-side validation: strip any products that no longer exist (ignore custom builds)
        const productIds = patched
          .filter((i) => i.productId && !i.customConfig)
          .map((i) => i.productId as string);
        if (productIds.length > 0) {
          fetch(`/api/products?ids=${encodeURIComponent(productIds.join(","))}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
              if (!data?.products) return;
              const validIds = new Set<string>(data.products.map((p: { id: string }) => p.id));
              const cleaned = patched.filter(
                (i) => !i.productId || !!i.customConfig || validIds.has(i.productId)
              );
              if (cleaned.length !== patched.length) {
                dispatch({ type: "LOAD", payload: cleaned });
                // Best-effort warning — toast system not injected here, so use console
                console.warn("Some cart items were removed because they are no longer available.");
              }
            })
            .catch(() => {
              // Silent failure — keep existing cart if the server can't be reached
            });
        }
      } catch {}
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(state.items));
  }, [state.items]);

  // ─── Abandoned-cart recovery sync ───
  // Keep a CartSession row in the DB for logged-in users so the
  // /api/cron/abandoned-carts job can email them if they walk away.
  // Previously CartSession was ONLY created when the user hit the
  // /checkout page, which meant "added item to cart, closed the tab"
  // generated zero email reminders. Now every cart mutation pushes
  // to the server (debounced), so any logged-in user with items in
  // cart becomes eligible for the reminder sequence.
  //
  // Debounced 2s so rapid quantity changes coalesce into one request.
  const { data: session } = useSession();
  const isAuthed = !!session?.user?.id;
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasHydratedRef = useRef(false);

  useEffect(() => {
    // Skip the first render — loading items from localStorage shouldn't
    // trigger a sync, only actual user actions.
    if (!hasHydratedRef.current) {
      hasHydratedRef.current = true;
      return;
    }
    if (!isAuthed) return;
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      const payload = {
        items: state.items.map((i) => ({
          productId: i.productId,
          quantity: i.quantity,
          customConfig: i.customConfig,
          customPrice: i.price,
          productName: i.name,
        })),
        subtotal: state.items.reduce((s, i) => s + i.price * i.quantity, 0),
      };
      fetch("/api/cart-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {
        /* non-blocking — reminder email is a nice-to-have */
      });
    }, 2000);
    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    };
  }, [state.items, isAuthed]);

  // Auto-clear max quantity message after 3 seconds
  useEffect(() => {
    if (maxQuantityReached) {
      const timer = setTimeout(() => setMaxQuantityReached(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [maxQuantityReached]);

  const addItem = useCallback(
    (item: Omit<CartItemData, "id">) => {
      // Check if adding would exceed max
      const id = crypto.randomUUID();
      dispatch({ type: "ADD_ITEM", payload: { ...item, id } });
      setCartBounceKey((k) => k + 1);
      trackEvent("add_to_cart", {
        productId: item.productId || "custom",
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        isCustom: !!item.customConfig,
      });
    },
    []
  );

  const removeItem = useCallback((id: string) => {
    const item = state.items.find((i) => i.id === id);
    if (item) {
      trackEvent("cart_item_removed", { productId: item.productId || "custom", productName: item.name });
    }
    dispatch({ type: "REMOVE_ITEM", payload: id });
  }, [state.items]);

  // Admin-editable per-item cap. Falls back to the module-level
  // constant if SiteConfig hasn't loaded yet (first paint) so we never
  // accidentally drop the limit to 0.
  const { maxQuantityPerCartItem: cfgMaxQty } = useSiteConfig();
  const maxQty = cfgMaxQty > 0 ? cfgMaxQty : MAX_QUANTITY_PER_ITEM;

  const updateQuantity = useCallback((id: string, quantity: number) => {
    if (quantity > maxQty) {
      setMaxQuantityReached(id);
      return;
    }
    const item = state.items.find((i) => i.id === id);
    if (item) {
      trackEvent("cart_quantity_updated", {
        productId: item.productId || "custom",
        direction: quantity > item.quantity ? "increase" : "decrease",
        newQuantity: quantity,
      });
    }
    dispatch({ type: "UPDATE_QUANTITY", payload: { id, quantity } });
  }, [state.items, maxQty]);

  const setGiftWrap = useCallback((id: string, giftWrap: boolean, giftMessage?: string) => {
    dispatch({ type: "SET_GIFT_WRAP", payload: { id, giftWrap, giftMessage } });
    trackEvent("cart_gift_wrap_toggled", { itemId: id, giftWrap });
  }, []);

  const setGiftMessage = useCallback((id: string, giftMessage: string) => {
    dispatch({ type: "SET_GIFT_MESSAGE", payload: { id, giftMessage } });
  }, []);

  const clearMaxQuantityMessage = useCallback(() => {
    setMaxQuantityReached(null);
  }, []);

  const clearCart = useCallback(() => {
    trackEvent("cart_cleared");
    dispatch({ type: "CLEAR" });
  }, []);
  const toggleCart = useCallback(() => dispatch({ type: "TOGGLE_CART" }), []);
  const openCart = useCallback(() => dispatch({ type: "OPEN_CART" }), []);
  const closeCart = useCallback(() => dispatch({ type: "CLOSE_CART" }), []);

  const getCartItemsForCheckout = useCallback((): GuestCheckoutItem[] => {
    return state.items.map((item) => {
      if (item.productId) {
        return {
          productId: item.productId,
          quantity: item.quantity,
          giftWrap: !!item.giftWrap,
          giftMessage: item.giftWrap ? item.giftMessage || "" : undefined,
          // Forward the reserved-build binding so /api/checkout can
          // atomically claim the specific WigAsset.
          wigAssetId: item.wigAssetId,
        };
      }
      // Custom builder wig — no productId, send config + price so the server
      // can persist it as an OrderItem with isCustom=true (Bug 8)
      return {
        quantity: item.quantity,
        customConfig: item.customConfig,
        customPrice: item.price,
        productName: item.name || "Custom Wig",
        giftWrap: !!item.giftWrap,
        giftMessage: item.giftWrap ? item.giftMessage || "" : undefined,
      };
    });
  }, [state.items]);

  const subtotal = state.items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );
  const giftWrapTotal = state.items.reduce(
    (sum, item) => (item.giftWrap ? sum + GIFT_WRAP_PRICE * item.quantity : sum),
    0
  );
  const itemCount = state.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items: state.items,
        isOpen: state.isOpen,
        addItem,
        removeItem,
        updateQuantity,
        setGiftWrap,
        setGiftMessage,
        clearCart,
        toggleCart,
        openCart,
        closeCart,
        subtotal,
        giftWrapTotal,
        itemCount,
        maxQuantityReached,
        clearMaxQuantityMessage,
        getCartItemsForCheckout,
        cartBounceKey,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used within CartProvider");
  return context;
}
